/**
 * Websites API
 *
 * Manage website templates (public and admin views), creation with uploads,
 * and usage tracking.
 */
import { get, post, put, del } from '../core';

/**
 * Gets all websites for public access (auth required)
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.tags - Comma-separated tags
 * @param {string} options.sortBy - Sort field
 * @returns {Promise<{websites: Array, page: number, limit: number, hasMore: boolean}>} Websites data
 */
export function getAllWebsites(options = {}) {
  const params = new URLSearchParams();
  
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search) params.append('search', options.search);
  if (options.tags) params.append('tags', options.tags);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  
  return get(`/websites?${params.toString()}`);
}

/**
 * Save a website to a namespace
 * @param {string} websiteId - The website ID
 * @param {string} namespaceId - The namespace ID
 * @returns {Promise<Object>} Response data
 */
export const saveWebsite = async (websiteId, namespaceId) => {
  try {
    if (!namespaceId) {
      throw new Error('Namespace ID is required to save websites');
    }
    
    const response = await post(`/websites/save/${websiteId}?namespaceId=${encodeURIComponent(namespaceId)}`, {});
    return response.data;
  } catch (error) {
    console.error('Error saving website:', error);
    throw error;
  }
};

/**
 * Unsave a website from a namespace
 * @param {string} websiteId - The website ID
 * @param {string} namespaceId - The namespace ID
 * @returns {Promise<Object>} Response data
 */
export const unsaveWebsite = async (websiteId, namespaceId) => {
  try {
    if (!namespaceId) {
      throw new Error('Namespace ID is required to unsave websites');
    }
    
    const response = await del(`/websites/save/${websiteId}?namespaceId=${encodeURIComponent(namespaceId)}`);
    return response.data;
  } catch (error) {
    console.error('Error unsaving website:', error);
    throw error;
  }
};

/**
 * Get saved website IDs for a specific namespace
 * @param {string} namespaceId - The namespace ID
 * @returns {Promise<Array>} Array of saved website IDs
 */
export const getSavedWebsiteIds = async (namespaceId) => {
  try {
    if (!namespaceId) {
      console.warn('No namespace ID provided, cannot fetch saved websites');
      return [];
    }
    
    // Use the namespaces endpoint to get namespace details including savedWebsites
    const namespaceData = await get(`/namespaces/${namespaceId}`);
    
    // Extract savedWebsites from the namespace data
    if (namespaceData && Array.isArray(namespaceData.savedWebsites)) {
      return namespaceData.savedWebsites;
    }
    
    // In case the savedWebsites array doesn't exist
    return [];
  } catch (error) {
    // If endpoint doesn't exist (404), return empty array
    if (error.message?.includes('404') || error.status === 404) {
      console.log('Websites saved endpoint not yet implemented, returning empty array');
      return [];
    }
    console.error('Error getting saved website IDs:', error);
    return []; // Return empty array on error to avoid breaking the UI
  }
};

/**
 * Gets a website upload URL from the server
 * @returns {Promise<{uploadUrl: string, permanentUploadPath: string}>} Upload URL and permanent path
 */
export function getWebsiteUploadUrl() {
  return get('/websites/video-upload-url');
}

/**
 * Creates a new website template
 * @param {FormData} formData - The website form data
 * @returns {Promise<{message: string, websiteId: string}>} Success response
 */
export function createWebsite(formData) {
  return post('/websites/create', formData);
}

/**
 * Gets all websites for admin management
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.tags - Comma-separated tags
 * @param {string} options.sortBy - Sort field
 * @returns {Promise<{websites: Array, totalCount: number, currentPage: number, totalPages: number}>} Websites data
 */
export function getAllWebsitesForAdmin(options = {}) {
  const params = new URLSearchParams();
  
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search) params.append('search', options.search);
  if (options.tags) params.append('tags', options.tags);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  
  return get(`/websites/all?${params.toString()}`);
}

/**
 * Gets a single website by ID
 * @param {string} websiteId - The website ID
 * @returns {Promise<{website: Object}>} Website data
 */
export function getWebsite(websiteId) {
  return get(`/websites/${websiteId}`);
}

/**
 * Updates an existing website template
 * @param {string} websiteId - The website ID
 * @param {FormData} formData - The form data containing updated fields
 * @returns {Promise<{message: string}>} Success response
 */
export function updateWebsite(websiteId, formData) {
  return put(`/websites/${websiteId}`, formData);
}

/**
 * Deletes a website template
 * @param {string} websiteId - The website ID
 * @returns {Promise<{message: string}>} Success response
 */
export function deleteWebsite(websiteId) {
  return del(`/websites/${websiteId}`);
}

/**
 * Record website usage when a user clicks the website link
 * @param {string} websiteId - The ID of the website being used
 * @returns {Promise<Object>} - The success response
 */
export async function useWebsite(websiteId) {
  try {
    return await post(`/websites/use/${websiteId}`, {});
  } catch (error) {
    console.error('Error recording website usage:', error);
    // Don't throw the error - this is a non-critical operation
    return null;
  }
}

/**
 * Generate website metadata (title, tags, search terms) using AI based on URL
 * @param {string} websiteUrl - The URL of the website to analyze
 * @returns {Promise<Object>} AI-generated website data
 */
export async function generateWebsiteWithAI(websiteUrl) {
  try {
    const response = await post('/websites/generate-with-ai', { websiteUrl });
    // post() already returns the parsed JSON data, no need to access .data
    return response;
  } catch (error) {
    console.error('Error generating website with AI:', error);
    throw error;
  }
}

